import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { InspectionChecklist, InspectionChecklistQuestion } from '@otw/models'

const INSPECTION_CHECKLIST_COLLECTION = 'inspection_checklist'
/** Default Complete (inspection) checklist document id. */
export const DEFAULT_CHECKLIST_DOC_ID = 'default'
/** Default Intake checklist document id. */
export const DEFAULT_INTAKE_CHECKLIST_DOC_ID = 'default-intake'

export type InspectionChecklistKind = 'intake' | 'complete'

const defaultDocIdForKind = (kind: InspectionChecklistKind): string =>
  kind === 'intake' ? DEFAULT_INTAKE_CHECKLIST_DOC_ID : DEFAULT_CHECKLIST_DOC_ID

const defaultNameForKind = (kind: InspectionChecklistKind): string =>
  kind === 'intake' ? 'Default Intake Checklist' : 'Default Complete Checklist'

const buildDefaultChecklist = (
  checklistId: string,
  kind: InspectionChecklistKind
): InspectionChecklist => ({
  id: checklistId,
  name:
    checklistId === DEFAULT_CHECKLIST_DOC_ID || checklistId === DEFAULT_INTAKE_CHECKLIST_DOC_ID
      ? defaultNameForKind(kind)
      : kind === 'intake'
        ? 'Intake Checklist'
        : 'Complete Checklist',
  kind,
  questions: [],
})

interface UseInspectionChecklistOptions {
  checklistId?: string
  fallbackToDefault?: boolean
  /** Controls naming and which global default to fall back to. */
  kind?: InspectionChecklistKind
}

export const useInspectionChecklist = (options?: UseInspectionChecklistOptions) => {
  const queryClient = useQueryClient()
  const kind: InspectionChecklistKind = options?.kind || 'complete'
  const checklistId = options?.checklistId || defaultDocIdForKind(kind)
  const fallbackToDefault = options?.fallbackToDefault ?? true
  const fallbackDocId = defaultDocIdForKind(kind)
  const checklistDocRef = doc(db, INSPECTION_CHECKLIST_COLLECTION, checklistId)

  const {
    data: checklist = buildDefaultChecklist(checklistId, kind),
    isLoading,
    error,
  } = useQuery({
    queryKey: ['inspection_checklist', checklistId, kind],
    queryFn: async () => {
      const snapshot = await getDoc(checklistDocRef)

      if (snapshot.exists()) {
        const data = snapshot.data() as Partial<InspectionChecklist>
        return {
          id: snapshot.id,
          name: data.name || defaultNameForKind(kind),
          kind: data.kind || kind,
          questions: Array.isArray(data.questions) ? data.questions : [],
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        } as InspectionChecklist
      }

      if (fallbackToDefault && checklistId !== fallbackDocId) {
        const defaultDocRef = doc(db, INSPECTION_CHECKLIST_COLLECTION, fallbackDocId)
        const defaultSnapshot = await getDoc(defaultDocRef)
        if (defaultSnapshot.exists()) {
          const defaultData = defaultSnapshot.data() as Partial<InspectionChecklist>
          return {
            id: defaultSnapshot.id,
            name: defaultData.name || defaultNameForKind(kind),
            kind: defaultData.kind || kind,
            questions: Array.isArray(defaultData.questions) ? defaultData.questions : [],
            createdAt: defaultData.createdAt,
            updatedAt: defaultData.updatedAt,
          } as InspectionChecklist
        }
      }

      return buildDefaultChecklist(checklistId, kind)
    },
  })

  const { mutateAsync: saveChecklist, isPending: isSaving } = useMutation({
    mutationFn: async (payload: {
      questions: InspectionChecklistQuestion[]
      name?: string
      kind?: InspectionChecklistKind
    }) => {
      const now = Timestamp.now()
      const saveKind = payload.kind || kind
      const normalizedQuestions = payload.questions.map((question, index) => ({
        ...question,
        order: index,
        answerOptions: ['yes', 'no', 'na'],
      }))

      await setDoc(
        checklistDocRef,
        {
          name: payload.name || checklist.name || defaultNameForKind(saveKind),
          questions: normalizedQuestions,
          kind: saveKind,
          createdAt: checklist.createdAt || now,
          updatedAt: now,
        },
        { merge: true }
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['inspection_checklist', checklistId],
      })
    },
  })

  const sortedQuestions = useMemo(() => {
    return [...(checklist.questions || [])].sort((a, b) => a.order - b.order)
  }, [checklist.questions])

  return {
    checklist,
    questions: sortedQuestions,
    isLoading,
    isSaving,
    saveChecklist,
    error: error instanceof Error ? error.message : null,
  }
}

export default useInspectionChecklist
